import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import {openExploreChat} from 'relevant-items/openExploreChat';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

const OVERVIEW_PROMPT =
  "Give me an overview of this entity's history — what it was derived from, what's been created from it, what workflows it has been a part of, and any key experiments or results.";

interface ExploreItemViaAIChatButtonProps {
  fileId: string;
}

/**
 * Button that triggers an AI-generated overview of the current file.
 * Opens the AI sidepanel with Ask mode and a comprehensive overview prompt.
 */
export default function ExploreItemViaAIChatButton({
  fileId,
}: ExploreItemViaAIChatButtonProps): JSX.Element | null {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isRegistryEntity = APIUtil.isBiologicalEntity(fileId);
  const isEnabled =
    BenchlingConfig.LLM_RELEVANT_ITEMS_EXPLORE_BUTTON &&
    BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH &&
    isRegistryEntity;

  // Track when button is viewed
  useEffect(() => {
    if (isEnabled) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_RELEVANT_ITEMS_EXPLORE_BUTTON, {
        fileId,
      });
    }
  }, [fileId, isEnabled]);

  if (!isEnabled) {
    return null;
  }

  const handleClick = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const chatSessionId = await openExploreChat(OVERVIEW_PROMPT, fileId);

      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_RELEVANT_ITEMS_EXPLORE_BUTTON, {
        fileId,
        chatSessionId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      size={ButtonSize.SMALL}
      icon="sparkle"
      text={isSubmitting ? <LLMLoadingMessage text="Opening chat..." /> : 'Explore'}
      onClick={handleClick}
      disabled={isSubmitting}
      dataTestAttrs={{
        component: 'ExploreItemViaAIChatButton',
        element: 'button',
      }}
    />
  );
}
